import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import moment from "moment";
import "moment/locale/es";
import { useNavigate } from "react-router-dom";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import mobileDeviceServices from "app/services/mobile-device-services";

const MANAGERS = ["superadmin", "customer_admin", "supervisor"];

const ago = (value) => (value ? moment(value).locale("es").fromNow() : "-");
const date = (value) => (value ? moment(value).format("DD/MM/YYYY HH:mm") : "-");

const deviceLabel = (device) => {
    if (!device) return null;
    return [device.description, device.app_version && `app ${device.app_version}`].filter(Boolean).join(" · ");
};

// Un telefono por cuenta de conductor. Cada cuenta se abre en un telefono a la vez y tiene un cupo
// de cambios por mes; pasado el cupo, el telefono nuevo espera que alguien de la empresa lo apruebe
// aca. Ver hermes-backend app/services/licensing/device_binding.rb.
const DevicesIndex = () => {
    const navigate = useNavigate();
    const { authUser } = useJumboAuth();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(null);
    const [releasing, setReleasing] = useState(null);
    const [error, setError] = useState(null);

    const allowed = useMemo(() => {
        const companyId = authUser?.current_company?.id ?? authUser?.current_company_id;
        return (authUser?.profiles || []).some((p) => p.name === "superadmin" || (MANAGERS.includes(p.name) && `${p.company_id}` === `${companyId}`));
    }, [authUser]);

    useEffect(() => {
        if (authUser && !allowed) navigate("/trips");
    }, [authUser, allowed, navigate]);

    const load = useCallback(() => {
        mobileDeviceServices.list().then(setData).catch(() => setData({ data: [], meta: {} }));
    }, []);

    useEffect(() => {
        if (allowed) load();
    }, [allowed, load]);

    const act = async (key, fn) => {
        setBusy(key);
        setError(null);
        try {
            await fn();
            load();
        } catch (e) {
            setError(e?.response?.data?.error || "No se pudo completar la acción");
        } finally {
            setBusy(null);
        }
    };

    if (!allowed) return null;

    const rows = data?.data || [];
    const limit = data?.meta?.switch_limit ?? 2;
    const pendingCount = data?.meta?.pending_count || 0;

    return (
        <Stack spacing={2}>
            <div>
                <Typography variant="h2" sx={{ mb: 0.5 }}>Dispositivos</Typography>
                <Typography variant="body2" color="text.secondary">
                    Cada cuenta de conductor se usa en un teléfono a la vez: si alguien ingresa desde otro, el anterior queda afuera.
                    Cada cuenta puede cambiar de teléfono {limit} veces por mes; desde la siguiente, el cambio se aprueba acá.
                </Typography>
            </div>

            {pendingCount > 0 && (
                <Alert severity="warning">
                    {pendingCount === 1 ? "Hay 1 teléfono esperando aprobación." : `Hay ${pendingCount} teléfonos esperando aprobación.`} La persona vuelve a
                    ingresar con el mismo código apenas lo apruebes.
                </Alert>
            )}
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

            <Card variant="outlined">
                <CardContent>
                    {!data ? (
                        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress color="warning" /></Stack>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Conductor</TableCell>
                                        <TableCell>Teléfono vinculado</TableCell>
                                        <TableCell>Vinculado desde</TableCell>
                                        <TableCell>Último uso</TableCell>
                                        <TableCell align="right">Cambios del mes</TableCell>
                                        <TableCell>Esperando aprobación</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row) => (
                                        <TableRow key={row.user_id} hover>
                                            <TableCell>
                                                <b>{row.name}</b>
                                                {row.exempt && <Chip size="small" label="Sin restricción" sx={{ ml: 1, height: 20 }} />}
                                            </TableCell>
                                            <TableCell>{deviceLabel(row.device) || <Typography variant="body2" color="text.secondary">Ninguno todavía</Typography>}</TableCell>
                                            <TableCell>{date(row.device?.bound_at || row.bound_at)}</TableCell>
                                            <TableCell>{ago(row.device?.last_seen_at)}</TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" color={row.switches_this_month >= limit ? "warning.main" : "text.primary"}>
                                                    {row.switches_this_month} de {limit}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {row.pending.length === 0 ? "-" : row.pending.map((device) => (
                                                    <Stack key={device.id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }} flexWrap="wrap" useFlexGap>
                                                        <Typography variant="body2">{deviceLabel(device)} <Typography component="span" variant="caption" color="text.secondary">({ago(device.last_seen_at)})</Typography></Typography>
                                                        <Button size="small" variant="contained" color="warning" disabled={busy !== null}
                                                                onClick={() => act(`a${device.id}`, () => mobileDeviceServices.approve(device.id))}>
                                                            Aprobar
                                                        </Button>
                                                        <Button size="small" color="inherit" disabled={busy !== null}
                                                                onClick={() => act(`r${device.id}`, () => mobileDeviceServices.reject(device.id))}>
                                                            Rechazar
                                                        </Button>
                                                    </Stack>
                                                ))}
                                            </TableCell>
                                            <TableCell align="right">
                                                {row.device && (
                                                    <Button size="small" disabled={busy !== null} onClick={() => setReleasing(row)}>
                                                        Desvincular
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {rows.length === 0 && (
                                        <TableRow><TableCell colSpan={7}><Typography color="text.secondary">No hay conductores en esta empresa.</Typography></TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            <Dialog open={Boolean(releasing)} onClose={() => setReleasing(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Desvincular el teléfono de {releasing?.name}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        La sesión en {releasing?.device?.description || "ese teléfono"} se cierra en su próximo uso. El próximo teléfono desde
                        el que ingrese quedará vinculado sin gastar uno de sus cambios del mes. Úsalo cuando el teléfono se perdió, se rompió o se lo
                        cambiaron.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button color="inherit" onClick={() => setReleasing(null)}>Cancelar</Button>
                    <Button variant="contained" color="warning" disabled={busy !== null}
                            onClick={() => {
                                const row = releasing;
                                setReleasing(null);
                                act(`l${row.user_id}`, () => mobileDeviceServices.release(row.user_id));
                            }}>
                        Desvincular
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default DevicesIndex;
